import { laravelGraphqlEndpoint } from '@/globals/globals'
import { ProjectComment } from '@/types/projectComment'
import { User } from '@/types/user'
import { Box, Button, Link, Textarea, useToast } from '@chakra-ui/react'
import { captureEvent } from '@sentry/nextjs'
import { gql, GraphQLClient } from 'graphql-request'
import { Dispatch, FormEvent, SetStateAction, useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

type Props = {
  allComments?: ProjectComment[]
  parent_uuid?: string
  project_uuid: string
  setComments: Dispatch<SetStateAction<ProjectComment[]>>
  setShowForm?: Dispatch<SetStateAction<boolean>>
  user?: User
}

export const ProjectCommentForm = (props: Props) => {
  const { parent_uuid, project_uuid } = props
  const [comment, setComment] = useState<string>()
  const toast = useToast()
  const [canComment, setCanComment] = useState<boolean>(false)

  useEffect(() => {
    if (!props.user) return
    if (props.user.email_verified_at) setCanComment(true)
  }, [props.user])

  if (!props.user?.uuid) return null

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    const graphQLClient = new GraphQLClient(laravelGraphqlEndpoint)
    const mutation = gql`
      mutation createMemberProjectComment(
        $author_uuid: String!
        $comment: String!
        $parent_uuid: String
        $project_uuid: String!
        $uuid: ID!
      ) {
        createMemberProjectComment(
          author_uuid: $author_uuid
          comment: $comment
          parent_uuid: $parent_uuid
          project_uuid: $project_uuid
          uuid: $uuid
        ) {
          uuid
          comment
          parent_uuid
          author_uuid
          project_uuid
          created_at
          user {
            avatar
            first_name
            uuid
          }
        }
      }
    `

    let variables = {
      author_uuid: props.user?.uuid,
      comment,
      parent_uuid,
      project_uuid,
      uuid: uuidv4(),
    }

    await graphQLClient
      .request(mutation, variables)
      .then(r => {
        toast({ title: 'Comment submitted!' })
        setComment('')
        if (props.setShowForm) props.setShowForm(false)
        if (props.allComments)
          props.setComments([
            ...props.allComments,
            r.createMemberProjectComment,
          ])
      })
      .catch(error => {
        toast({ title: 'Failed!', status: 'error' })
        captureEvent(error)
      })
  }

  if (!props.user) {
    return (
      <>
        <Box>
          To leave a comment,{' '}
          <Link textDecoration='underline' href='/register'>
            sign up
          </Link>{' '}
          or{' '}
          <Link textDecoration='underline' href='/login'>
            login
          </Link>
        </Box>
      </>
    )
  }

  if (!canComment) {
    return (
      <>
        <Box>
          To leave a comment,{' '}
          <Link textDecoration='underline' href='/verify-email' target='_blank'>
            Verify your email address
          </Link>
        </Box>
      </>
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      <Textarea
        mb={2}
        height={24}
        onChange={e => {
          setComment(e.target.value)
        }}
        value={comment}></Textarea>
      <Button type='submit'>Submit Comment</Button>
    </form>
  )
}
